import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  QrCodeScanner as QrIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as CarIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { flatsAPI } from '../services/api';
import SecurityGuardQRScanner from '../components/SecurityGuardQRScanner';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`security-tabpanel-${index}`}
      aria-labelledby={`security-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SecurityGuardDashboard = () => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [vehicleLogs, setVehicleLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Fetch vehicle entry logs
  const fetchVehicleLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        limit: 50
      };
      
      if (filterDate) {
        params.date = filterDate;
      }
      
      const response = await flatsAPI.getVehicleEntryLogs(params);
      
      const logs = response.data.logs || [];
      
      setVehicleLogs(logs);
      
    } catch (error) {
      console.error('Error fetching vehicle logs:', error);
      setError('Failed to fetch vehicle logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (currentTab === 1) { // History tab
      fetchVehicleLogs();
    }
  }, [currentTab, filterDate]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        align="center" 
        color="primary"
        sx={{
          fontSize: { xs: '1.5rem', md: '2rem' },
          mb: { xs: 2, md: 3 }
        }}
      >
        {t('securityGuard.dashboard.title', 'Security Guard Dashboard')}
      </Typography>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="security guard tabs"
              variant={window.innerWidth < 600 ? "fullWidth" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab 
                label={t('securityGuard.dashboard.qrScanner', 'QR Scanner')} 
                icon={<QrIcon />} 
                iconPosition="start"
                sx={{ 
                  minHeight: { xs: 48, md: 72 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              />
              <Tab 
                label={t('securityGuard.dashboard.history', 'Entry History')} 
                icon={<HistoryIcon />} 
                iconPosition="start"
                sx={{ 
                  minHeight: { xs: 48, md: 72 },
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              />
            </Tabs>
          </Box>

          {/* QR Scanner Tab */}
          <TabPanel value={currentTab} index={0}>
            <SecurityGuardQRScanner />
          </TabPanel>

          {/* Entry History Tab */}
          <TabPanel value={currentTab} index={1}>
            <Box mb={3}>
              <TextField
                type="date"
                label={t('securityGuard.dashboard.filterDate', 'Filter by Date')}
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                sx={{ maxWidth: 300 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('securityGuard.dashboard.timestamp', 'Timestamp')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.vehicleNumber', 'Vehicle Number')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.action', 'Action')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.flatNumber', 'Flat')}</TableCell>
                    <TableCell>{t('securityGuard.dashboard.buildingId', 'Building')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicleLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary" py={4}>
                          {loading 
                            ? t('common.loading', 'Loading...') 
                            : t('securityGuard.dashboard.noLogs', 'No vehicle logs found for the selected filters')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    vehicleLogs.map((log, index) => (
                      <TableRow key={log.logId || index}>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(log.timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {log.vehicleNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color={log.action === 'ENTRY' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{log.flatId}</TableCell>
                        <TableCell>{log.buildingId}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>


        </Box>
      </Container>
  );
};

export default SecurityGuardDashboard;